import axios from "axios";

const BACKEND = "https://api.rangpurgolf.com";
// const BACKEND = "http://localhost:8081";

const api = axios.create({
    baseURL: `${BACKEND}/api`,
    withCredentials: true, // send cookies for refresh token
});

// Attach access token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("token");
        if (token) {
            config.headers = config.headers || {};
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Shared in-flight refresh promise so concurrent 401s trigger a single /refresh
// call instead of one per failed request - they all await the same promise and
// retry with whatever token it resolves to.
let refreshPromise = null;

function refreshAccessToken() {
    if (!refreshPromise) {
        refreshPromise = api
            .post(`/refresh`, {}, { withCredentials: true })
            .then((res) => {
                const newAccessToken = res.data.accessToken;
                if (!newAccessToken) {
                    throw new Error("No access token returned from refresh");
                }
                localStorage.setItem("token", newAccessToken);
                api.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
                return newAccessToken;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
}

// Response interceptor for auto-refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        // Don't handle login request failures - let them show the error message
        if (originalRequest.url?.includes('/member/login') || originalRequest.url?.includes('/admin/login')
            || originalRequest.url?.includes('/refresh')
        ) {
            return Promise.reject(error);
        }

        // Only retry once per request for authenticated routes
        if ((status === 401 || status === 403) && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const newAccessToken = await refreshAccessToken();
                originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
                return api(originalRequest);
            } catch (refreshErr) {
                console.warn("Refresh token failed:", refreshErr);

                // Clear all auth-related data
                localStorage.removeItem("token");
                localStorage.removeItem("memberEmail");
                localStorage.removeItem("dashboardLocked");
                localStorage.removeItem("paymentStatus");
                localStorage.removeItem("registrationStatus");

                // Only redirect if we're not already on the login page
                if (!window.location.pathname.includes('/login')) {
                    window.location.replace("/login");
                }
            }
        }

        return Promise.reject(error);
    }
);

export default api;
